package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.strings.EditorStrings
import io.kvision.core.onClick
import io.kvision.form.check.CheckBoxInput
import io.kvision.form.check.checkBoxInput
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.span
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.Node

/** Every classifier visibility this codebase's `Visibility` enum knows, in cycle order -- see
 * [cycleVisibility]. Mirrors `dev.kuml.uml.Visibility.entries` by hand (this module hand-mirrors
 * every server enum it touches, same convention as `ModelOpCodec.kt`'s class KDoc).
 *
 * `internal`, not `private` (review fix, MINOR, 2026-09-02): [cycleVisibility] is pure and directly
 * unit-testable, so it -- and [parseMultLower]/[parseMultUpper] below, same reasoning -- are widened
 * from file-private to module-internal purely so `InspectorPanelTest.kt` can call them, matching this
 * module's "pure logic gets a direct test, DOM glue stays manually verified" convention (see
 * `DiagramCanvasTest.kt`'s own class KDoc). */
internal val VISIBILITY_CYCLE = listOf("PUBLIC", "PRIVATE", "PROTECTED", "PACKAGE")

internal fun cycleVisibility(current: String?): String {
    val index = VISIBILITY_CYCLE.indexOf(current).let { if (it < 0) 0 else it }
    return VISIBILITY_CYCLE[(index + 1) % VISIBILITY_CYCLE.size]
}

/** `""`/blank parses to `0` -- the same "empty means the default" convention [parseMultUpper]
 * uses for "unbounded" (`*`), just with a different default, since a lower bound has no server-
 * side sentinel for "unspecified" the way an upper bound's `null` does. */
internal fun parseMultLower(raw: String): Int = raw.trim().toIntOrNull() ?: 0

/** `null` (unbounded, `*` in DSL/inspector copy) for blank or literal `*`; otherwise the parsed
 * integer, or `null` again for anything that fails to parse -- mirrors the server's own
 * `UpdateAssociationEnd.multUpper: Int? = 1` default posture of "not a value worth rejecting
 * client-side, the interpreter's own limits are authoritative" ([ModelOpCodec]'s class KDoc). */
internal fun parseMultUpper(raw: String): Int? {
    val trimmed = raw.trim()
    if (trimmed.isEmpty() || trimmed == "*") return null
    return trimmed.toIntOrNull()
}

/**
 * The [ClientUpdateAttribute] a committed attribute-row edit in [renderFeatureList] sends -- pulled
 * out as a pure, directly testable function (review fix, MAJOR, 2026-09-02). An earlier revision
 * built this op INLINE at the `onUpdate` call site with a hardcoded `visibility = "PUBLIC"` and no
 * `defaultValue`/`isStatic`/`isReadOnly` at all -- since [ClientUpdateAttribute]'s op-side fields are
 * NOT "leave unchanged" sentinels (`DiagramLoroMutator.updateAttribute` unconditionally `copy()`s the
 * existing feature with every one of these fields taken from the op), every committed rename/type
 * edit through that form silently reset visibility to PUBLIC and dropped the feature's default value
 * and static/read-only flags. [feature] carries every one of those fields already (`/outline`'s
 * [OutlineFeature]); this function's whole job is to copy them across untouched, changing only
 * [newName]/[newTypeName].
 */
internal fun updateAttributeOpFor(
    classId: String,
    feature: OutlineFeature,
    newName: String,
    newTypeName: String,
): ClientUpdateAttribute =
    ClientUpdateAttribute(
        classId = classId,
        attrId = feature.id,
        name = newName,
        typeName = newTypeName,
        visibility = feature.visibility,
        defaultValue = feature.defaultValue,
        isStatic = feature.isStatic,
        isReadOnly = feature.isReadOnly,
    )

/** The operation-row mirror of [updateAttributeOpFor] -- same MAJOR review fix, same reasoning:
 * [feature]'s [OutlineFeature.visibility]/[OutlineFeature.isAbstract]/[OutlineFeature.isStatic] are
 * preserved rather than reset to PUBLIC/`false`/`false`. */
internal fun updateOperationOpFor(
    classId: String,
    feature: OutlineFeature,
    newName: String,
    newReturnTypeName: String,
): ClientUpdateOperation =
    ClientUpdateOperation(
        classId = classId,
        opId = feature.id,
        name = newName,
        returnTypeName = newReturnTypeName.ifBlank { null },
        visibility = feature.visibility,
        isAbstract = feature.isAbstract,
        isStatic = feature.isStatic,
    )

/** Kinds [DiagramLoroMutator]'s `FEATURE_OWNER_KINDS` allows attributes/operations on -- an
 * enumeration has neither (see that object's KDoc), so [renderNode] never offers the "+
 * Attribut"/"+ Methode" affordances for one. */
private val FEATURE_OWNER_KINDS = setOf("class", "interface")

/** Kinds [DiagramLoroMutator]'s `setClassFlags` accepts -- interfaces have NO property in the
 * interpreter, not even visibility (see that op's KDoc), so [renderNode] only ever offers the
 * visibility/abstract controls for a `"class"`-kind element. */
private const val CLASS_KIND = "class"

/** Rendered by [inspectorPanel] for [InspectorContent.Edge] -- kinds [DiagramLoroMutator]'s
 * `RENAMABLE_KINDS` excludes (generalization/realization/dependency) get a plain read-only label
 * plus delete, never a rename field the server would just reject. */
private val RENAMABLE_KINDS = setOf("class", "interface", "enumeration", "association")

/** @property update re-renders the panel for a new [InspectorContent] -- called by
 *   `EditorScreen.kt` every time [InspectorModel.contentFor] produces a new value (a click, an
 *   Escape, or a post-refresh [DiagramSelection.restore]). */
public class InspectorPanelHandle(
    public val update: (InspectorContent) -> Unit,
)

/**
 * R-27's inspector panel -- renders exactly one [InspectorContent] variant at a time, entirely out
 * of pure child-widget calls (this codebase's `content =`-vs-child-widgets binding convention,
 * see `EditorScreen.kt`'s `applyAttachedContext`'s KDoc). Every committed edit becomes exactly one
 * [ClientModelOp] passed to [onOp] -- this function never talks to [CollabClient] directly, it only
 * ever calls back; `EditorScreen.kt` owns the actual `collab.sendOp(...)` call, matching this
 * module's established "DOM/business logic here, wire glue in EditorScreen" split.
 *
 * @param onSelectEdge invoked when the user clicks one of [InspectorContent.Node.relations]'s
 *   entries -- selecting that relationship is a SELECTION change, not a [ClientModelOp], so it goes
 *   through its own callback rather than [onOp].
 */
public fun Div.inspectorPanel(
    strings: EditorStrings,
    onOp: (ClientModelOp) -> Unit,
    onSelectEdge: (String) -> Unit,
): InspectorPanelHandle {
    val root = this

    fun render(content: InspectorContent) {
        root.removeAll()
        when (content) {
            InspectorContent.None -> renderNone(root, strings, onOp)
            is InspectorContent.Node -> renderNode(root, strings, content, onOp, onSelectEdge)
            is InspectorContent.TwoNodes -> renderTwoNodes(root, strings, content, onOp)
            is InspectorContent.Edge -> renderEdge(root, strings, content, onOp)
        }
    }

    /** `true` while a genuine TEXT field inside this panel -- an `<input>` that is not a checkbox,
     * or a `<textarea>` -- currently has focus. A `<button>` or a checkbox is deliberately excluded:
     * both already commit their op immediately on click (see [renderDeleteButton]'s confirm button,
     * the abstract/navigable checkboxes above) rather than on blur, so a refresh landing while one of
     * those holds focus is always safe to apply immediately. */
    fun isEditingTextField(): Boolean {
        val rootElement = root.getElement() ?: return false
        val active = document.activeElement
        return active != null &&
            active != rootElement &&
            isDescendantOf(active, rootElement) &&
            (active.tagName == "TEXTAREA" || (active.tagName == "INPUT" && (active as? HTMLInputElement)?.type != "checkbox"))
    }

    // The most recent [InspectorContent] `update` below could not apply immediately because a text
    // field held focus -- applied by [flushPendingContent] the moment focus actually leaves this
    // panel. `null` whenever there is nothing suppressed to catch up on.
    var pendingContent: InspectorContent? = null

    /** Applies [pendingContent] once focus has genuinely left every text field in this panel --
     * the catch-up half of the focus guard below. Deliberately re-checks [isEditingTextField]
     * rather than assuming "a focusout fired, so focus must be gone": Tab moving focus from the
     * name field to the type field of the SAME row also fires `focusout` on the panel (it bubbles),
     * and must NOT flush mid-edit any more than the original guard would have. */
    fun flushPendingContent() {
        val pending = pendingContent ?: return
        if (isEditingTextField()) return
        pendingContent = null
        render(pending)
    }

    // Lazily attached the first time `root` actually has a DOM element (review fix, MAJOR,
    // 2026-09-02 -- see [update]'s KDoc). `focusout` (unlike `blur`) bubbles, so one listener on
    // `root`'s own element catches every field this panel ever renders, present or future, without
    // any of the render functions below needing to know this guard exists. Not part of KVision's
    // typed `setEventListener` DSL for the same reason `EditorScreen.kt`'s zoom/pan wiring uses raw
    // `addEventListener` -- `focusout` isn't one of the event kinds that DSL covers.
    var focusOutListenerAttached = false

    fun ensureFocusOutListenerAttached() {
        if (focusOutListenerAttached) return
        val rootElement = root.getElement() ?: return
        focusOutListenerAttached = true
        // Deferred one tick: at the moment `focusout` itself fires, `document.activeElement` is not
        // yet guaranteed to reflect the element focus is MOVING TO (e.g. the sibling field Tab is
        // about to land on) -- the same race [commitOnBlurOrEnter]'s boundary check below defers
        // for, and for the identical reason.
        rootElement.addEventListener("focusout", { window.setTimeout({ flushPendingContent() }, 0) })
    }

    /**
     * [render], guarded against tearing down a text field the user is CURRENTLY typing into (review
     * fix, MINOR, 2026-09-02), with the suppressed content applied the moment focus actually leaves
     * (review fix, MINOR round 2, 2026-09-02 -- see [flushPendingContent]). `EditorScreen.kt`'s
     * `refreshInspector` calls this after EVERY `MODEL_REV` -- including the one this very
     * connection's own just-committed op round-trips back as (the server broadcasts `MODEL_REV` to
     * the sender too, see `CollabRoomRegistry.applyOpAndBroadcast`'s KDoc) -- and [render]
     * unconditionally `root.removeAll()`s and rebuilds every child widget from scratch, which steals
     * DOM focus no matter what currently holds it. Committing one field via Tab (blur -> commit ->
     * send -> the network round-trip for that op's own `MODEL_REV` -> this call) reliably landed on
     * the NEXT field Tab had already moved focus to, mid-keystroke, yanking it out of the DOM and
     * dropping every character typed since.
     *
     * **Round 1** suppressed such a refresh outright -- correct for as long as the field kept focus,
     * but the suppressed content was never applied afterwards, so the panel stayed stale
     * INDEFINITELY once the guard first triggered, not just "briefly" as that revision's KDoc claimed
     * (e.g. a collaborator deleting the very element being typed into left the full editing form,
     * delete button included, on screen forever -- see [flushPendingContent]'s own KDoc for the full
     * scenario). **Round 2** keeps the suppression but remembers the newest suppressed content in
     * [pendingContent] and applies it via [flushPendingContent] the moment [isEditingTextField]
     * next reports `false` -- which also means an Enter-commit (the field keeps focus after Enter,
     * see [commitOnBlurOrEnter]'s own KDoc) now eventually shows the server's actually-accepted
     * value (e.g. a name [UmlIds.disambiguate] altered) once the user tabs or clicks away, instead of
     * never showing it for the rest of that field's focus session.
     */
    fun update(content: InspectorContent) {
        ensureFocusOutListenerAttached()
        if (isEditingTextField()) {
            pendingContent = content
            return
        }
        pendingContent = null
        render(content)
    }

    render(InspectorContent.None)
    return InspectorPanelHandle(update = ::update)
}

private fun renderNone(
    root: Div,
    strings: EditorStrings,
    onOp: (ClientModelOp) -> Unit,
) {
    root.div(strings.inspectorNoSelectionHint, className = "kportal-inspector-hint")
    val addClass = root.button(strings.inspectorAddClassButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
    // A generic default name -- the server-side `UmlIds.disambiguate` call `addClass` already goes
    // through (see `DiagramLoroMutator.kt`) makes a second/third "Klasse" collision-free on its
    // own ("Klasse~2", ...), exactly like AddClass's own KDoc/test ("Order"/"Order~2") documents.
    // There is deliberately no rename-and-focus follow-up here: applyOpAndBroadcast's
    // `createdElementId` never reaches this connection (see `CollabRoutes.kt`'s MODEL_OP branch --
    // only a generic MODEL_REV goes out), so the new class simply appears in the next outline
    // refresh, ready to be clicked and renamed like any other element.
    addClass.onClick { onOp(ClientAddClass(name = strings.inspectorAddClassButton.removePrefix("+ ").trim())) }
}

private fun renderTwoNodes(
    root: Div,
    strings: EditorStrings,
    content: InspectorContent.TwoNodes,
    onOp: (ClientModelOp) -> Unit,
) {
    root.div(strings.inspectorTwoSelectedHint, className = "kportal-inspector-hint")
    root.div(className = "kportal-inspector-two-nodes") {
        span(content.first.name)
        span(" ↔ ")
        span(content.second.name)
    }
    val createButton =
        root.button(strings.inspectorCreateAssociationButton, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
    createButton.onClick { onOp(ClientAddAssociation(sourceId = content.first.id, targetId = content.second.id)) }
}

private fun renderNode(
    root: Div,
    strings: EditorStrings,
    content: InspectorContent.Node,
    onOp: (ClientModelOp) -> Unit,
    onSelectEdge: (String) -> Unit,
) {
    val element = content.element

    root.div(className = "kportal-inspector-field") {
        div(strings.inspectorNameLabel, className = "kportal-label")
        val nameInput = textInput(value = element.name, className = "kportal-input")
        commitOnBlurOrEnter(nameInput) { value ->
            val trimmed = value.trim()
            if (trimmed.isNotEmpty() && trimmed != element.name) onOp(ClientRenameElement(element.id, trimmed))
        }
    }

    if (element.kind == CLASS_KIND) {
        root.div(className = "kportal-inspector-field") {
            div(strings.inspectorVisibilityLabel, className = "kportal-label")
            // A local mutable var, not `element.visibility` re-read on every click (review fix,
            // MINOR, 2026-09-02): `element` is the immutable OutlineElement this ONE render pass
            // was built from, and stays that way until the next outline refresh lands -- a second
            // click before that round-trip completes previously re-derived the SAME "next value
            // from PUBLIC" every time, making a fast double-click a no-op (PUBLIC -> PRIVATE ->
            // PUBLIC -> PRIVATE, back where it started) instead of advancing the cycle twice.
            var currentVisibility = element.visibility ?: "PUBLIC"
            val visibilityButton =
                button(currentVisibility, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
            visibilityButton.onClick {
                currentVisibility = cycleVisibility(currentVisibility)
                visibilityButton.text = currentVisibility
                onOp(ClientSetClassFlags(element.id, visibility = currentVisibility))
            }
        }
        root.div(className = "kportal-inspector-field kportal-inspector-checkbox-field") {
            val abstractCheck: CheckBoxInput = checkBoxInput(value = element.isAbstract)
            span(strings.inspectorAbstractLabel)
            abstractCheck.onClick { onOp(ClientSetClassFlags(element.id, isAbstract = abstractCheck.value)) }
        }
    }

    if (element.kind in FEATURE_OWNER_KINDS) {
        renderFeatureList(
            root = root,
            heading = strings.inspectorAttributesHeading,
            addLabel = strings.inspectorAddAttributeButton,
            classId = element.id,
            features = element.attributes,
            onAdd = { name, typeName -> onOp(ClientAddAttribute(classId = element.id, name = name, typeName = typeName)) },
            onUpdate = { feature, name, typeName -> onOp(updateAttributeOpFor(element.id, feature, name, typeName)) },
            onDelete = { attrId -> onOp(ClientDeleteAttribute(classId = element.id, attrId = attrId)) },
        )
        renderFeatureList(
            root = root,
            heading = strings.inspectorOperationsHeading,
            addLabel = strings.inspectorAddOperationButton,
            classId = element.id,
            features = element.operations,
            onAdd = { name, returnTypeName ->
                onOp(ClientAddOperation(classId = element.id, name = name, returnTypeName = returnTypeName.ifBlank { null }))
            },
            onUpdate = { feature, name, returnTypeName -> onOp(updateOperationOpFor(element.id, feature, name, returnTypeName)) },
            onDelete = { opId -> onOp(ClientDeleteOperation(classId = element.id, opId = opId)) },
        )
    }

    root.div(className = "kportal-inspector-section") {
        div(strings.inspectorRelationshipsHeading, className = "kportal-label")
        if (content.relations.isEmpty()) {
            div("—", className = "kportal-inspector-hint")
        } else {
            content.relations.forEach { relation ->
                val row = div(relation.name.ifBlank { relation.kind }, className = "kportal-inspector-relation-row")
                row.onClick { onSelectEdge(relation.id) }
            }
        }
    }

    renderDeleteButton(root, strings, element.name, content.relations.size) { onOp(ClientDeleteElement(element.id)) }
}

private fun renderEdge(
    root: Div,
    strings: EditorStrings,
    content: InspectorContent.Edge,
    onOp: (ClientModelOp) -> Unit,
) {
    val element = content.element

    if (element.kind in RENAMABLE_KINDS) {
        root.div(className = "kportal-inspector-field") {
            div(strings.inspectorNameLabel, className = "kportal-label")
            val nameInput = textInput(value = element.name, className = "kportal-input")
            commitOnBlurOrEnter(nameInput) { value ->
                val trimmed = value.trim()
                if (trimmed != element.name) onOp(ClientRenameElement(element.id, trimmed))
            }
        }
    } else {
        root.div("${element.kind}: ${element.name}".trim(), className = "kportal-inspector-hint")
    }

    if (element.kind == "association") {
        element.ends.forEachIndexed { index, end ->
            root.div(className = "kportal-inspector-field") {
                div("${strings.inspectorRelationshipsHeading} ${index + 1}", className = "kportal-label")
                val roleInput = textInput(value = end.role.orEmpty(), className = "kportal-input")
                roleInput.placeholder = strings.inspectorNameLabel
                val lowerInput = textInput(value = end.multLower.toString(), className = "kportal-input kportal-inspector-mult-input")
                val upperInput =
                    textInput(value = end.multUpper?.toString() ?: "*", className = "kportal-input kportal-inspector-mult-input")
                val navigableCheck: CheckBoxInput = checkBoxInput(value = end.navigable)

                fun commitEnd() {
                    onOp(
                        ClientUpdateAssociationEnd(
                            assocId = element.id,
                            endIndex = index,
                            role =
                                roleInput.value
                                    ?.trim()
                                    .orEmpty()
                                    .ifBlank { null },
                            multLower = parseMultLower(lowerInput.value.orEmpty()),
                            multUpper = parseMultUpper(upperInput.value.orEmpty()),
                            navigable = navigableCheck.value,
                        ),
                    )
                }
                commitOnBlurOrEnter(roleInput) { commitEnd() }
                commitOnBlurOrEnter(lowerInput) { commitEnd() }
                commitOnBlurOrEnter(upperInput) { commitEnd() }
                navigableCheck.onClick { commitEnd() }
            }
        }
    }

    renderDeleteButton(root, strings, element.name, cascadeCount = 0) { onOp(ClientDeleteElement(element.id)) }
}

/**
 * One attribute/operation list -- shared by [renderNode] for both [OutlineElement.attributes] and
 * [OutlineElement.operations] (the two lists' inspector treatment is identical: name + type/return
 * type, click-to-edit-in-place, delete). [onAdd] takes the raw name/type-name pair rather than a
 * full [ClientModelOp] -- the caller supplies which concrete op (attribute vs. operation) that pair
 * becomes. [onUpdate] instead gets the full, un-modified [OutlineFeature] alongside the edited
 * name/type-name (review fix, MAJOR, 2026-09-02, see [updateAttributeOpFor]/[updateOperationOpFor]'s
 * own KDoc) -- an edit only ever changes the two fields this form actually exposes, and the caller
 * needs every OTHER field [OutlineFeature] already carries (visibility, defaultValue, isStatic, ...)
 * to build an update op that does not silently reset them.
 */
private fun renderFeatureList(
    root: Div,
    heading: String,
    addLabel: String,
    classId: String,
    features: List<OutlineFeature>,
    onAdd: (name: String, typeName: String) -> Unit,
    onUpdate: (feature: OutlineFeature, name: String, typeName: String) -> Unit,
    onDelete: (featureId: String) -> Unit,
) {
    root.div(className = "kportal-inspector-section") {
        div(heading, className = "kportal-label")
        val listHost = div()

        fun renderRow(feature: OutlineFeature) {
            val row = listHost.div(className = "kportal-inspector-feature-row")
            val label = feature.typeName?.let { "${feature.name}: $it" } ?: feature.name

            // Kotlin local functions cannot forward-reference one another (renderCollapsed needs
            // to call renderExpanded, and vice versa on commit) -- both are declared as function-
            // typed `lateinit var`s first and assigned below, so each can close over the OTHER's
            // var before it holds a real function yet; neither is ever actually INVOKED before
            // both assignments below have run (a click event, at the earliest).
            lateinit var renderCollapsed: () -> Unit
            lateinit var renderExpanded: () -> Unit

            renderCollapsed = {
                row.removeAll()
                val text = row.span(label, className = "kportal-inspector-feature-label")
                text.onClick { renderExpanded() }
                val deleteButton = row.button("×", className = "kportal-chip-remove")
                deleteButton.onClick { onDelete(feature.id) }
            }

            renderExpanded = {
                row.removeAll()
                val nameInput = row.textInput(value = feature.name, className = "kportal-input")
                val typeInput = row.textInput(value = feature.typeName.orEmpty(), className = "kportal-input")
                // Guards against Enter immediately followed by the blur it triggers committing
                // TWICE -- unlike a rename's [ClientRenameElement] (naturally idempotent, a
                // second identical call is a harmless no-op), sending [onUpdate] twice is still
                // only wasteful, not wrong (same feature id, same fields) -- but the guard is
                // cheap and keeps this symmetric with the add-row's guard below, where a second
                // send would NOT be harmless (see that guard's own comment).
                var committed = false

                fun commit() {
                    if (committed) return
                    val name = nameInput.value?.trim().orEmpty()
                    val typeName = typeInput.value?.trim().orEmpty()
                    if (name.isEmpty()) return
                    committed = true
                    onUpdate(feature, name, typeName)
                    renderCollapsed()
                }
                // `row.getElement()` as the commit boundary (review fix, MAJOR, 2026-09-02 -- see
                // [commitOnBlurOrEnter]'s own KDoc): without it, Tab from the name field to the type
                // field blurred the name field FIRST, committing with the type field's still-
                // unedited value and tearing the whole row down (`renderCollapsed()` above) out from
                // under the focus Tab had just moved there.
                val rowElement = row.getElement()
                commitOnBlurOrEnter(nameInput, rowElement) { commit() }
                commitOnBlurOrEnter(typeInput, rowElement) { commit() }
            }

            renderCollapsed()
        }

        features.forEach(::renderRow)

        val addButton = div(addLabel, className = "kportal-chip")
        addButton.onClick {
            val row = listHost.div(className = "kportal-inspector-feature-row")
            val nameInput = row.textInput(className = "kportal-input")
            val typeInput = row.textInput(className = "kportal-input")
            // Unlike the edit-row's `commit()` above, a double-send HERE would create the
            // attribute/operation TWICE (AddAttribute/AddOperation are never idempotent -- each
            // call appends a fresh feature, see DiagramLoroMutator's `addAttribute`/`addOperation`)
            // -- Enter immediately followed by the blur it triggers must only ever fire [onAdd] once.
            var committed = false

            fun commit() {
                if (committed) return
                val name = nameInput.value?.trim().orEmpty()
                val typeName = typeInput.value?.trim().orEmpty()
                if (name.isEmpty()) return
                committed = true
                onAdd(name, typeName)
                row.removeAll()
                row.visible = false
            }
            // `row.getElement()` as the commit boundary (review fix, MAJOR, 2026-09-02 -- see
            // [commitOnBlurOrEnter]'s own KDoc): without it, Tab from the (still-empty) name field
            // to the type field committed on the name field's blur with `typeName = ""`, which
            // `DiagramLoroMutator.validateTypeName` rejects server-side -- silently tearing down
            // the very type field the user's Tab keypress had just moved focus into, on every
            // attempt to fill the fields in their on-screen left-to-right order.
            val rowElement = row.getElement()
            commitOnBlurOrEnter(nameInput, rowElement) { commit() }
            commitOnBlurOrEnter(typeInput, rowElement) { commit() }
        }
    }
}

/** A "Löschen" button that, on click, swaps itself for an inline confirm/cancel row (per the
 * design-team's confirmation-dialog copy in [EditorStrings]) rather than deleting immediately --
 * [cascadeCount] > 0 additionally shows [EditorStrings.confirmDeleteCascadeWarning] (a classifier
 * whose deletion cascades to the relationships touching it, see `DiagramLoroMutator.deleteElement`'s
 * KDoc "Complexity"/transitive-cascade discussion). */
private fun renderDeleteButton(
    root: Div,
    strings: EditorStrings,
    elementName: String,
    cascadeCount: Int,
    onConfirmed: () -> Unit,
) {
    val row = root.div(className = "kportal-inspector-delete-row")
    val deleteButton = row.button(strings.inspectorDeleteButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
    deleteButton.onClick {
        row.removeAll()
        row.div(strings.confirmDeleteTitle, className = "kportal-label")
        row.div(strings.confirmDeleteQuestion(elementName), className = "kportal-inspector-hint")
        if (cascadeCount > 0) {
            row.div(strings.confirmDeleteCascadeWarning(cascadeCount), className = "kportal-inspector-hint")
        }
        val confirmButton = row.button(strings.confirmDeleteConfirmButton, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
        val cancelButton =
            row.button(
                strings.confirmDeleteCancelButton,
                className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto",
            )
        confirmButton.onClick { onConfirmed() }
        cancelButton.onClick {
            // Removes THIS row's own DOM node before rebuilding a fresh one (review fix, MAJOR,
            // 2026-09-02) -- `renderDeleteButton` below creates a brand-new `root.div(...)` row of
            // its own; without tearing this one down first, cancelling left the fully-armed confirm/
            // cancel row (its `confirmButton.onClick` above still live) sitting in the DOM, with a
            // second "Löschen" button appended underneath it. A stray click on that still-live
            // confirm button deleted the element despite the user having just cancelled, and every
            // further cancel cycle appended yet another delete row, growing the panel without bound.
            // `.getElement()?.remove()` (raw DOM removal), not `row.removeAll()` (which would only
            // empty this row's children, still leaving an empty row div behind) -- same convention
            // `DiagramTabs.kt`/`ProjectFilesPanel.kt` already use for discarding a widget's own node.
            row.getElement()?.remove()
            renderDeleteButton(root, strings, elementName, cascadeCount, onConfirmed)
        }
    }
}

/** `true` if [node] is [ancestor] itself or a descendant of it -- walks [Node.parentNode] by hand
 * rather than depending on the DOM's own `Node.contains`, which this codebase's Kotlin/JS DOM API
 * surface does not otherwise call anywhere (`parentNode`, by contrast, is exactly the kind of
 * bog-standard property every DOM binding here already exposes). Used by [inspectorPanel]'s focus
 * guard above AND by [commitOnBlurOrEnter]'s boundary check below (review fix, MAJOR, 2026-09-02 --
 * both are exactly the same "has focus actually left this subtree" question). */
private tailrec fun isDescendantOf(
    node: Node?,
    ancestor: Element,
): Boolean =
    when {
        node == null -> false
        node == ancestor -> true
        else -> isDescendantOf(node.parentNode, ancestor)
    }

/**
 * Commits [onCommit] on blur (focus lost) AND on Enter (without waiting for the blur that
 * ordinarily follows it) -- mirrors `EditorScreen.kt`'s `startRename`'s own keydown/blur pairing.
 * A value committed via Enter and then again via the following blur is an accepted, harmless
 * double-send (idempotent on the server: the second op is identical to the first), not worth the
 * extra `settled`-flag bookkeeping `startRename` uses for its cancel-on-Escape behaviour, which
 * none of these fields offer (there is no "revert to the pre-edit value" affordance here).
 *
 * [boundary], when given, makes the blur-commit conditional on focus actually LEAVING [boundary]
 * (review fix, MAJOR, 2026-09-02) -- pass the container `Element` of a multi-field ROW here (e.g.
 * an attribute/operation row's name+type pair) whenever committing tears that row's fields down
 * (`renderCollapsed()`/`row.removeAll()`): without it, Tab moving focus from one field of the row
 * to the OTHER blurs the first field, which commits using the second field's still-unedited value
 * and then destroys both fields out from under the focus Tab had just moved to. The check is
 * deferred one tick via `window.setTimeout` because at the instant `blur` itself fires,
 * `document.activeElement` is not yet guaranteed to reflect the element focus is moving TO (Tab's
 * own destination, in particular) -- by the next tick it reliably does. `null` (the default) keeps
 * the original immediate-commit behaviour, correct for every single-field case (a plain rename,
 * one association-end field) where blurring never tears anything else down.
 */
private fun commitOnBlurOrEnter(
    input: TextInput,
    boundary: Element? = null,
    onCommit: (String) -> Unit,
) {
    input.setEventListener<TextInput> {
        keydown = { e -> if (e.key == "Enter") onCommit(input.value.orEmpty()) }
        blur = {
            if (boundary == null) {
                onCommit(input.value.orEmpty())
            } else {
                val value = input.value.orEmpty()
                window.setTimeout({
                    val active = document.activeElement
                    val stillWithinBoundary = active != null && isDescendantOf(active, boundary)
                    if (!stillWithinBoundary) onCommit(value)
                }, 0)
            }
        }
    }
}
